import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { supabaseConfigError } from '../lib/supabase';
import appLogo from '../assets/logo.svg';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (supabaseConfigError) {
      setError(supabaseConfigError);
      return;
    }
    setLoading(true);

    const { error } = await signIn(email, password);
    if (error) {
      setError('Email ou mot de passe incorrect.');
      setLoading(false);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={appLogo}
            alt="Logo"
            className="h-16 w-auto object-contain mb-4"
          />
          <h1 className="text-xl font-bold text-foreground">Prévisionnel Financier</h1>
          <p className="text-sm text-muted-foreground mt-1">My Notary — Outil interne</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Connexion</CardTitle>
            <CardDescription>Accès réservé aux membres de l'équipe</CardDescription>
          </CardHeader>
          <CardContent>
            {supabaseConfigError && (
              <div className="mb-4 flex items-start gap-2 text-sm text-danger bg-danger/5 border border-danger/20 rounded-md px-3 py-2">
                <Icon icon="lucide:alert-triangle" className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  <strong>Configuration manquante.</strong> {supabaseConfigError}
                </span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="vous@mynotary.co.uk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:underline"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && (
                <div className="flex items-center gap-2 text-sm text-danger bg-danger/5 border border-danger/20 rounded-md px-3 py-2">
                  <Icon icon="lucide:alert-circle" className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Icon icon="lucide:loader-2" className="w-4 h-4 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  <>
                    <Icon icon="lucide:log-in" className="w-4 h-4" />
                    Se connecter
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
